import React, { useCallback, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';

interface SubmitTranslationModalProps {
  visible: boolean;
  loading: boolean;
  onDismiss: () => void;
  onConfirm: (notificationEmail: string) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Confirmation modal that collects a notification email before submitting
 * a string group for translation.
 */
const SubmitTranslationModal: React.FC<SubmitTranslationModalProps> = ({
  visible,
  loading,
  onDismiss,
  onConfirm,
}) => {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);

  const isValid = EMAIL_REGEX.test(email.trim());
  const showError = touched && !isValid;

  const handleConfirm = useCallback(() => {
    setTouched(true);
    if (!isValid) {
      return;
    }
    onConfirm(email.trim());
    // The parent hides this modal without unmounting it, so clear the field
    // here too — otherwise the next open shows the previous email and error.
    setEmail('');
    setTouched(false);
  }, [email, isValid, onConfirm]);

  const handleDismiss = useCallback(() => {
    setEmail('');
    setTouched(false);
    onDismiss();
  }, [onDismiss]);

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Submit for translation"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={handleDismiss} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={loading}
              disabled={!email.trim()}
            >
              Submit
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          label="Notification email"
          description="You will receive a notification at this address when translation completes."
          errorText={
            showError ? 'Please enter a valid email address.' : undefined
          }
        >
          <Input
            value={email}
            onChange={({ detail }) => setEmail(detail.value)}
            onBlur={() => setTouched(true)}
            placeholder="you@amazon.com"
            type="email"
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};

export default SubmitTranslationModal;
